import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { getApiRoot } from "@ugg/shared/api/api-helpers";
import { useUGGApiVersions } from "@ugg/shared/api/requests/ugg-api-versions";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";

// Data Parsers
import { parseArenaDuoTierList } from "@ugg/shared/api/data-parser/tier-lists/arena-duo-tier-list";

// Interfaces
import { ArenaDuoTierList } from "@ugg/shared/interfaces/tier-lists/arena-duo-tier-list.interface";

// ------------------------------------------------------------------------------------------------------------

interface TierListParams {
  region: string;
  patch: string;
}

// ------------------------------------------------------------------------------------------------------------
// ARENA DUO TIER LIST
// ------------------------------------------------------------------------------------------------------------

function useArenaDuoTierListURL(params: TierListParams) {
  const { data: version } = useUGGApiVersions({
    apiKey: "duos",
    patch: params.patch,
  });
  const root = getApiRoot(params.patch);

  return [`${root}/duos`, params.region, params.patch, "arena", "overall", `${version}.json`].join("/");
}

export function useArenaDuoTierList<K = ReturnType<typeof parseArenaDuoTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<ArenaDuoTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARENA_DUO_TIER_LIST, params, true);
  const url = useArenaDuoTierListURL(validatedQueryParams);

  if (onCompleted) {
    return useJSONFetcher<ArenaDuoTierList, K>(url, {
      skip,
      ssr,
      customCacheKey,
      onCompleted,
    });
  }

  return useJSONFetcher<ArenaDuoTierList, ReturnType<typeof parseArenaDuoTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => parseArenaDuoTierList(json),
  });
}
